import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { 
    HelpCircle, 
    FileText, 
    TrendingUp, 
    ClipboardList, 
    Hourglass, 
    CheckCircle2, 
    Clock, 
    UserCheck, 
    Download, 
    GraduationCap, 
    Network 
} from 'lucide-react';
import { 
    PieChart, 
    Pie, 
    Cell, 
    Tooltip as RechartsTooltip, 
    ResponsiveContainer 
} from 'recharts';
import { API_BASE_URL } from "../../../../api";

const DashboardRecrutementRH = () => {
    // ----------------------------------------------------
    // Tab management and date filters
    // ----------------------------------------------------
    const [activeTab, setActiveTab] = useState('kpi');
    const [dateDebut, setDateDebut] = useState('2026-06-02');
    const [dateFin, setDateFin] = useState('2026-06-02');

    // Get current user from Redux store for download endpoints
    const auth = useSelector((state) => state.auth) || {};
    const user = auth.user || { id: 1 };

    const tabs = [
        { id: 'kpi', label: 'KPI', icon: HelpCircle },
        { id: 'etats', label: 'ÉTATS', icon: FileText },
        { id: 'forecast', label: 'FORECAST', icon: TrendingUp }
    ];

    // ----------------------------------------------------
    // KPI Tab data
    // ----------------------------------------------------
    const kpiCardsData = [
        { value: '25', label: 'Demandes de recrutement', icon: ClipboardList },
        { value: '70%', label: 'Demandes de recrutement en attente', icon: Hourglass },
        { value: '30%', label: 'Demandes de recrutement traitées', icon: CheckCircle2 },
        { value: '10', label: 'Candidatures en cours', icon: Clock },
        { value: '14%', label: 'Candidatures traitées', icon: CheckCircle2 },
        { value: '9%', label: 'Candidats Intégrés', icon: UserCheck }
    ];

    const demandesStatusData = [
        { name: 'En attente', value: 5, color: '#35649d' },
        { name: 'Validée', value: 8, color: '#ffa61f' },
        { name: 'Rejetée', value: 3, color: '#dc3131' },
        { name: 'En cours', value: 6, color: '#66a162' },
        { name: 'Terminée', value: 2, color: '#5bc0de' },
        { name: 'Annulée', value: 1, color: '#4D869C' }
    ];

    const candidaturesStatusData = [
        { name: 'En attente', value: 4, color: '#35649d' },
        { name: 'Validée', value: 6, color: '#ffa61f' },
        { name: 'Rejetée', value: 2, color: '#dc3131' },
        { name: 'Offre envoyée', value: 5, color: '#4D869C' },
        { name: 'Traitement Administratif', value: 3, color: '#5bc0de' },
        { name: 'Confirmée', value: 1, color: '#66a162' }
    ];

    const entretiensStatusData = [
        { name: 'Planifié', value: 5, color: '#35649d' },
        { name: 'Non planifié', value: 3, color: '#ffa61f' },
        { name: 'Réalisé', value: 7, color: '#66a162' },
        { name: 'Reporté', value: 2, color: '#5bc0de' },
        { name: 'Manqué', value: 1, color: '#4D869C' },
        { name: 'Annulé', value: 2, color: '#ff5b5b' }
    ];

    const donutChartsData = [
        { title: 'Status des demandes de recrutement', data: demandesStatusData },
        { title: 'Status des candidatures', data: candidaturesStatusData },
        { title: 'Status des entretiens', data: entretiensStatusData }
    ];

    // ----------------------------------------------------
    // ÉTATS Tab data and handler
    // ----------------------------------------------------
    const etatsCardsData = [
        {
            title: "États des demandes de recrutement",
            icon: HelpCircle,
            fileName: "Demandes_recrutement.xlsx",
            endpoint: "/module/guest/user/module/completion/etat/manager/{userId}/{dateDebut}/{dateFin}"
        },
        {
            title: "États des candidature",
            icon: ClipboardList,
            fileName: "Etats_candidatures.xlsx",
            endpoint: "/userEvaluationDts/guest/evaluation/etat/manager/{userId}/{dateDebut}/{dateFin}"
        },
        {
            title: "États des candidats intégrés",
            icon: GraduationCap,
            fileName: "Candidats_integres.xlsx",
            endpoint: "/module/guest/user/module/etat/manager/{userId}/{dateDebut}/{dateFin}"
        },
        {
            title: "Suivi des entretiens de recrutement",
            icon: Network,
            fileName: "Suivi_entretiens_recrutement.xlsx",
            endpoint: "/userEvaluationDts/guest/evaluation/froid/etat/responsable/{userId}/{dateDebut}/{dateFin}"
        },
        {
            title: "Rapports sur le processus de sélection",
            icon: UserCheck,
            fileName: "Rapports_processus_selection.xlsx",
            endpoint: "/absence/guest/absences/etat/manager/{userId}/{dateDebut}/{dateFin}"
        },
        {
            title: "Analyse de la qualité des profils recrutés",
            icon: TrendingUp,
            fileName: "Analyse_qualite_profils_recrutes.xlsx",
            endpoint: "/campagne/guest/concolidation/etat/manager/{userId}"
        }
    ];

    const downloadState = async (url, filename, dateDebutStr = null, dateFinStr = null) => {
        try {
            let finalUrl = url;
            if (dateDebutStr && dateFinStr) {
                finalUrl = url.replace('{dateDebut}', dateDebutStr).replace('{dateFin}', dateFinStr);
            }

            const response = await fetch(finalUrl, {
                method: 'GET',
                headers: {
                    'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    ...(localStorage.getItem('token') ? { 'Authorization': `Bearer ${localStorage.getItem('token')}` } : {})
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = downloadUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(downloadUrl);
            document.body.removeChild(a);

        } catch (error) {
            console.error('Erreur lors du téléchargement:', error);
            alert('Erreur lors du téléchargement du fichier Excel. Veuillez réessayer.');
        }
    };

    const handleDownload = (card) => {
        const userId = user?.id || 1;
        let url = card.endpoint
            .replace('{userId}', userId)
            .replace('{dateDebut}', dateDebut)
            .replace('{dateFin}', dateFin);
        
        const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
        downloadState(fullUrl, card.fileName, dateDebut, dateFin);
    };

    // ----------------------------------------------------
    // FORECAST Tab logic (Existing forecast logic)
    // ----------------------------------------------------
    const [filters, setFilters] = useState({
        département: 'all',
        année: '2026',
        période: 'q1',
    });

    const mockData = {
        départements: [
            { id: 'all', name: 'Tous les départements', mult: 1 },
            { id: 'engineering', name: 'Ingénierie & Tech', mult: 0.45 },
            { id: 'sales', name: 'Ventes & Marketing', mult: 0.35 },
            { id: 'hr', name: 'Ressources Humaines', mult: 0.1 },
            { id: 'finance', name: 'Finance', mult: 0.2 },
        ],
        années: ['2026', '2025'],
        périodes: [
            { id: 'q1', name: 'Trimestre 1 (Jan - Mar)', months: ['JAN', 'FÉV', 'MAR'] },
            { id: 'q2', name: 'Trimestre 2 (Avr - Juin)', months: ['AVR', 'MAI', 'JUIN'] },
            { id: 'q3', name: 'Trimestre 3 (Juil - Sept)', months: ['JUIL', 'AOÛT', 'SEPT'] },
            { id: 'q4', name: 'Trimestre 4 (Oct - Déc)', months: ['OCT', 'NOV', 'DÉC'] },
        ],
    };

    const generateData = (deptId, année, périodeId) => {
        const dept = mockData.départements.find((d) => d.id === deptId);
        const m = dept.mult;

        const profiles = {
            q1: {
                rec: [34, 28],
                with: [11, 14],
                load: [78, 62],
                turn: [4.5, 4.2],
                hires: [95, 88, 125],
                exits: [22, 28, 42],
                just: {
                    rec: 'IA Sourcing & scoring automatisé',
                    with: 'Agressivité des chasseurs de tête Q1',
                    load: 'Déploiement du nouveau portail RH',
                    turn: "Effet des entretiens annuels de Décembre",
                },
                action: "Renforcer les primes de cooptation pour compenser la hausse des désistements.",
            },
            q2: {
                rec: [38, 45],
                with: [14, 11],
                load: [80, 92],
                turn: [5.8, 6.5],
                hires: [110, 155, 172],
                exits: [28, 52, 78],
                just: {
                    rec: 'Pénurie sur les profils experts',
                    with: "Amélioration de l'onboarding digital",
                    load: 'Campagne de stages & alternance',
                    turn: 'Rotation cyclique après versement des bonus',
                },
                action: "Lancer une campagne de rétention ciblée sur les 'Key Talents' avant la fin du trimestre.",
            },
            q3: {
                rec: [32, 29],
                with: [10, 13],
                load: [71, 65],
                turn: [3.9, 3.4],
                hires: [82, 58, 135],
                exits: [32, 19, 42],
                just: {
                    rec: "Simplification du tunnel d'entretien",
                    with: "Relâchement de l'engagement estival",
                    load: "Recours temporaire à l'externalisation",
                    turn: 'Politique de flexibilité télétravail efficace',
                },
                action: "Maintenir le lien avec les candidats en vivier pour éviter l'érosion du pipe durant l'été.",
            },
            q4: {
                rec: [30, 35],
                with: [12, 16],
                load: [82, 88],
                turn: [4.2, 5.1],
                hires: [108, 92, 72],
                exits: [28, 38, 52],
                just: {
                    rec: 'Gel partiel des budgets recrutement',
                    with: "Contre-offres de fin d'année",
                    load: 'Clôture administrative et reporting',
                    turn: 'Départs à la retraite non anticipés',
                },
                action: 'Anticiper les besoins du Q1 2027 dès Novembre pour réduire la charge recruteur future.',
            },
        };

        const p = profiles[périodeId];

        const monthlyFlow = mockData.périodes
            .find((per) => per.id === périodeId)
            .months.map((name, idx) => {
                const hN1 = Math.floor(p.hires[idx] * m);
                const eN1 = Math.floor(p.exits[idx] * m);
                const hN = Math.floor(hN1 * (p.rec[1] > p.rec[0] ? 1.15 : 0.85));
                const eN = Math.floor(eN1 * (p.turn[1] > p.turn[0] ? 1.2 : 0.9));

                return {
                    name,
                    actualHires: hN1,
                    predHires: hN,
                    actualExits: eN1,
                    predExits: eN,
                    alert: eN > hN ? 'Risque de sous-effectif' : 'Flux de croissance',
                };
            });

        return {
            recruitmentTime: { vN: p.rec[1], vN1: p.rec[0], just: p.just.rec },
            withdrawalRate: { vN: p.with[1], vN1: p.with[0], just: p.just.with },
            recruiterLoad: { vN: p.load[1], vN1: p.load[0], just: p.just.load },
            turnover: { vN: p.turn[1], vN1: p.turn[0], just: p.just.turn },
            monthlyFlow,
            recommendedAction: p.action,
        };
    };

    const currentData = useMemo(
        () => generateData(filters.département, filters.année, filters.période),
        [filters]
    );

    const maxVal = useMemo(
        () =>
            Math.max(
                ...currentData.monthlyFlow.flatMap((d) => [
                    d.actualHires,
                    d.predHires,
                    d.actualExits,
                    d.predExits,
                ]),
                1
            ),
        [currentData]
    );

    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    return (
        <div
            style={{
                fontFamily: "var(--rc-font), 'Inter', sans-serif",
                background: '#ffffff',
                padding: '20px',
                minHeight: 'auto',
                boxSizing: 'border-box',
                width: '100%',
                maxWidth: '100%',
                overflowX: 'hidden',
            }}
        >
            <div style={{ maxWidth: '100%', margin: 'auto', boxSizing: 'border-box' }}>
                
                {/* ----------------------------------------------------
                    Tabs Navigation Headers
                   ---------------------------------------------------- */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '24px',
                    marginBottom: '35px',
                    borderBottom: '1px solid #e2e8f0',
                    paddingBottom: '8px'
                }}>
                    {tabs.map((tab) => {
                        const IconComponent = tab.icon;
                        const isActive = activeTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    background: 'none',
                                    border: 'none',
                                    borderBottom: isActive ? '3px solid #204385' : '3px solid transparent',
                                    padding: '8px 16px',
                                    color: isActive ? '#204385' : '#64748b',
                                    fontWeight: 'bold',
                                    fontSize: '0.9rem',
                                    textTransform: 'uppercase',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    outline: 'none',
                                    marginBottom: '-11px',
                                    fontFamily: 'inherit'
                                }}
                            >
                                <IconComponent size={18} color={isActive ? '#204385' : '#64748b'} />
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

                {/* ----------------------------------------------------
                    Tab 1: KPI Content
                   ---------------------------------------------------- */}
                {activeTab === 'kpi' && (
                    <div>
                        {/* Date Filters Boxed & Modern */}
                        <section style={{
                            background: '#fff',
                            padding: '15px',
                            borderRadius: '12px',
                            display: 'flex',
                            gap: '20px',
                            flexWrap: 'wrap',
                            maxWidth: '100%',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                            marginBottom: '25px',
                            boxSizing: 'border-box',
                        }}>
                            <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
                                <label style={{
                                    fontSize: '0.65rem',
                                    fontWeight: 900,
                                    color: '#94a3b8',
                                    textTransform: 'uppercase',
                                    display: 'block',
                                    marginBottom: '6px'
                                }}>
                                    De
                                </label>
                                <input 
                                    type="date" 
                                    value={dateDebut} 
                                    onChange={(e) => setDateDebut(e.target.value)}
                                    style={{
                                        padding: '8px 12px',
                                        borderRadius: '6px',
                                        border: '1px solid #e2e8f0',
                                        fontWeight: 400,
                                        fontSize: '0.85rem',
                                        background: '#f1f5f9',
                                        color: '#1e293b',
                                        cursor: 'pointer',
                                        width: '100%',
                                        maxWidth: '100%',
                                        boxSizing: 'border-box',
                                        outline: 'none',
                                        fontFamily: 'inherit'
                                    }}
                                />
                            </div>
                            <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
                                <label style={{
                                    fontSize: '0.65rem',
                                    fontWeight: 900,
                                    color: '#94a3b8',
                                    textTransform: 'uppercase',
                                    display: 'block',
                                    marginBottom: '6px'
                                }}>
                                    À
                                </label>
                                <input 
                                    type="date" 
                                    value={dateFin} 
                                    onChange={(e) => setDateFin(e.target.value)}
                                    style={{
                                        padding: '8px 12px',
                                        borderRadius: '6px',
                                        border: '1px solid #e2e8f0',
                                        fontWeight: 400,
                                        fontSize: '0.85rem',
                                        background: '#f1f5f9',
                                        color: '#1e293b',
                                        cursor: 'pointer',
                                        width: '100%',
                                        maxWidth: '100%',
                                        boxSizing: 'border-box',
                                        outline: 'none',
                                        fontFamily: 'inherit'
                                    }}
                                />
                            </div>
                        </section>

                        {/* KPI Cards Grid - 6 per line, smaller */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(6, 1fr)',
                            gap: '12px',
                            marginBottom: '35px',
                            width: '100%',
                            boxSizing: 'border-box'
                        }}>
                            {kpiCardsData.map((card, idx) => {
                                const CardIcon = card.icon;
                                return (
                                    <div 
                                        key={idx}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            background: '#fff',
                                            padding: '12px 16px',
                                            borderRadius: '12px',
                                            border: '1px solid #e2e8f0',
                                            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                                            transition: 'transform 0.2s, box-shadow 0.2s',
                                            cursor: 'pointer',
                                            minWidth: 0
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                            e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0,0,0,0.1)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.05)';
                                        }}
                                    >
                                        <div style={{
                                            marginRight: '12px',
                                            color: '#f97316',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            background: '#fff',
                                            borderRadius: '50%',
                                            padding: '8px',
                                            border: '1.5px solid #fdba74',
                                            flexShrink: 0
                                        }}>
                                            <CardIcon size={20} strokeWidth={2} />
                                        </div>
                                        <div style={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
                                            <div style={{ 
                                                fontSize: '1.3rem', 
                                                fontWeight: 'bold', 
                                                color: '#334155', 
                                                marginBottom: '2px',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}>
                                                {card.value}
                                            </div>
                                            <div style={{ 
                                                fontSize: '0.7rem', 
                                                color: '#64748b', 
                                                fontWeight: '500',
                                                lineHeight: '1.2',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                height: '2.4em'
                                            }}>
                                                {card.label}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Donut Charts Grid (Exactly 3 per line) */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: '20px',
                            marginBottom: '30px',
                            width: '100%'
                        }}>
                            {donutChartsData.map((chart, idx) => (
                                <div 
                                    key={idx}
                                    style={{
                                        background: '#fff',
                                        padding: '24px',
                                        borderRadius: '16px',
                                        border: '1px solid #e2e8f0',
                                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}
                                >
                                    <h3 style={{
                                        fontSize: '1rem',
                                        fontWeight: 'bold',
                                        color: '#204385',
                                        marginBottom: '16px',
                                        textAlign: 'left'
                                    }}>
                                        {chart.title}
                                    </h3>
                                    
                                    {/* Custom Legend */}
                                    <div style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '6px 12px',
                                        justifyContent: 'center',
                                        marginBottom: '20px',
                                        minHeight: '40px'
                                    }}>
                                        {chart.data.map((item, index) => (
                                            <div key={index} style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                fontSize: '0.75rem',
                                                fontWeight: '500',
                                                color: '#64748b'
                                            }}>
                                                <div style={{
                                                    width: '12px',
                                                    height: '12px',
                                                    background: item.color,
                                                    borderRadius: '3px'
                                                }} />
                                                <span>{item.name}</span>
                                            </div>
                                        ))}
                                    </div>

                                    <div style={{ width: '100%', height: 220 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={chart.data}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={55}
                                                    outerRadius={75}
                                                    paddingAngle={3}
                                                    dataKey="value"
                                                >
                                                    {chart.data.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                                    ))}
                                                </Pie>
                                                <RechartsTooltip 
                                                    contentStyle={{ background: '#fff', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                                                    itemStyle={{ color: '#1e293b', fontSize: '0.85rem' }}
                                                />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* ----------------------------------------------------
                    Tab 2: ÉTATS Content
                   ---------------------------------------------------- */}
                {activeTab === 'etats' && (
                    <div>
                        {/* Date Filters Boxed & Modern */}
                        <section style={{
                            background: '#fff',
                            padding: '15px',
                            borderRadius: '12px',
                            display: 'flex',
                            gap: '20px',
                            flexWrap: 'wrap',
                            maxWidth: '100%',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                            marginBottom: '25px',
                            boxSizing: 'border-box',
                        }}>
                            <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
                                <label style={{
                                    fontSize: '0.65rem',
                                    fontWeight: 900,
                                    color: '#94a3b8',
                                    textTransform: 'uppercase',
                                    display: 'block',
                                    marginBottom: '6px'
                                }}>
                                    De
                                </label>
                                <input 
                                    type="date" 
                                    value={dateDebut} 
                                    onChange={(e) => setDateDebut(e.target.value)}
                                    style={{
                                        padding: '8px 12px',
                                        borderRadius: '6px',
                                        border: '1px solid #e2e8f0',
                                        fontWeight: 400,
                                        fontSize: '0.85rem',
                                        background: '#f1f5f9',
                                        color: '#1e293b',
                                        cursor: 'pointer',
                                        width: '100%',
                                        maxWidth: '100%',
                                        boxSizing: 'border-box',
                                        outline: 'none',
                                        fontFamily: 'inherit'
                                    }}
                                />
                            </div>
                            <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
                                <label style={{
                                    fontSize: '0.65rem',
                                    fontWeight: 900,
                                    color: '#94a3b8',
                                    textTransform: 'uppercase',
                                    display: 'block',
                                    marginBottom: '6px'
                                }}>
                                    À
                                </label>
                                <input 
                                    type="date" 
                                    value={dateFin} 
                                    onChange={(e) => setDateFin(e.target.value)}
                                    style={{
                                        padding: '8px 12px',
                                        borderRadius: '6px',
                                        border: '1px solid #e2e8f0',
                                        fontWeight: 400,
                                        fontSize: '0.85rem',
                                        background: '#f1f5f9',
                                        color: '#1e293b',
                                        cursor: 'pointer',
                                        width: '100%',
                                        maxWidth: '100%',
                                        boxSizing: 'border-box',
                                        outline: 'none',
                                        fontFamily: 'inherit'
                                    }}
                                />
                            </div>
                        </section>

                        {/* États Cards Grid */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                            gap: '20px',
                            marginBottom: '35px'
                        }}>
                            {etatsCardsData.map((card, idx) => {
                                const CardIcon = card.icon;
                                return (
                                    <div 
                                        key={idx}
                                        onClick={() => handleDownload(card)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            background: '#fff',
                                            borderRadius: '12px',
                                            border: '1px solid #e2e8f0',
                                            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                                            overflow: 'hidden',
                                            transition: 'transform 0.2s, box-shadow 0.2s',
                                            cursor: 'pointer'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                            e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0,0,0,0.1)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.05)';
                                        }}
                                    >
                                        {/* Left Blue Box */}
                                        <div style={{
                                            width: '80px',
                                            height: '80px',
                                            background: '#204385',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#fff',
                                            flexShrink: 0
                                        }}>
                                            <CardIcon size={30} />
                                        </div>
                                        
                                        {/* Right Content */}
                                        <div style={{
                                            flex: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: '12px 16px',
                                            textAlign: 'center'
                                        }}>
                                            <div style={{
                                                color: '#204385',
                                                marginBottom: '6px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                <Download size={22} />
                                            </div>
                                            <div style={{
                                                fontSize: '0.85rem',
                                                color: '#475569',
                                                fontWeight: '600'
                                            }}>
                                                {card.title}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* ----------------------------------------------------
                    Tab 3: FORECAST Content
                   ---------------------------------------------------- */}
                {activeTab === 'forecast' && (
                    <div>
                        <header style={{ marginBottom: '20px' }}>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    color: '#204385',
                                    flexWrap: 'wrap',
                                    maxWidth: '100%',
                                }}
                            >
                                <h1 style={{  fontSize: '1.5rem', margin: 0, color: "#204385" ,fontFamily: "var(--rc-font)" }}>
                                    Analyse Prédictive
                                </h1>
                            </div>
                        </header>

                        {/* Barre de Filtres */}
                        <section
                            style={{
                                background: '#fff',
                                padding: '15px',
                                borderRadius: '12px',
                                display: 'flex',
                                gap: '20px',
                                flexWrap: 'wrap',
                                maxWidth: '100%',
                                border: '1px solid #e2e8f0',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                marginBottom: '20px',
                                boxSizing: 'border-box',
                            }}
                        >
                            {['département', 'année', 'période'].map((key) => {
                                const options = Array.isArray(mockData[key + 's']) ? mockData[key + 's'] : [];
                                const allowedValues = options.map((o) => (typeof o === 'object' ? o.id : o));
                                const safeValue = allowedValues.includes(filters[key])
                                    ? filters[key]
                                    : allowedValues.length > 0
                                        ? allowedValues[0]
                                        : '';

                                return (
                                    <div
                                        key={key}
                                        style={{
                                            minWidth: 200,
                                            flex: '1 1 200px',
                                            boxSizing: 'border-box',
                                        }}
                                    >
                                        <label
                                            style={{
                                                fontSize: '0.65rem',
                                                fontWeight: 900,
                                                color: '#94a3b8',
                                                textTransform: 'uppercase',
                                                display: 'block',
                                                marginBottom: '6px',
                                            }}
                                        >
                                            {key}
                                        </label>

                                        <select
                                            value={safeValue}
                                            onChange={(e) => handleFilterChange(key, e.target.value)}
                                            style={{
                                                padding: '8px 12px',
                                                borderRadius: '6px',
                                                border: '1px solid #e2e8f0',
                                                fontWeight: 400,
                                                fontSize: '0.85rem',
                                                background: '#f1f5f9',
                                                color: '#1e293b',
                                                cursor: 'pointer',
                                                width: '100%',
                                                maxWidth: '100%',
                                                boxSizing: 'border-box',
                                                outline: 'none',
                                                fontFamily: 'inherit'
                                            }}
                                        >
                                            {options.map((o) => {
                                                const value = typeof o === 'object' ? o.id : o;
                                                const label = typeof o === 'object' ? o.name : o;
                                                return (
                                                    <option key={value} value={value}>
                                                        {label}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </div>
                                );
                            })}
                        </section>

                        {/* Grille de KPIs */}
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                                gap: '15px',
                                marginBottom: '20px',
                                maxWidth: '100%',
                                boxSizing: 'border-box',
                            }}
                        >
                            <KPICard title="Délai Recrutement" data={currentData.recruitmentTime} unit=" J" invert />
                            <KPICard title="Taux Désistement" data={currentData.withdrawalRate} unit="%" invert />
                            <KPICard title="Charge Recruteur" data={currentData.recruiterLoad} unit="%" invert />
                            <KPICard title="Turnover" data={currentData.turnover} unit="%" invert />
                        </div>

                        {/* Section Graphique et Actions */}
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 1fr)',
                                gap: '15px',
                                maxWidth: '100%',
                                boxSizing: 'border-box',
                            }}
                        >
                            {/* Graphique */}
                            <div
                                style={{
                                    background: '#fff',
                                    padding: '25px',
                                    borderRadius: '15px',
                                    border: '1px solid #e2e8f0',
                                    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05)',
                                    maxWidth: '100%',
                                    boxSizing: 'border-box',
                                    overflowX: 'auto',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '30px',
                                        gap: '12px',
                                        flexWrap: 'wrap',
                                        maxWidth: '100%',
                                    }}
                                >
                                    <h2
                                        style={{
                                            fontSize: '0.75rem',
                                            fontWeight: 900,
                                            color: '#64748b',
                                            textTransform: 'uppercase',
                                            margin: 0,
                                        }}
                                    >
                                        Flux Mensuel : Réalisé (N-1) vs Prévisionnel (N)
                                    </h2>
                                    <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                                        <LegendItem c="#204385" l="RECRUTEMENTS" />
                                        <LegendItem c="#ee645f" l="DÉPARTS" />
                                        <LegendItem c="#64748b" l="POINTILLÉ = PRÉDIT" dotted />
                                    </div>
                                </div>

                                <div
                                    style={{
                                        height: '250px',
                                        display: 'flex',
                                        alignItems: 'flex-end',
                                        justifyContent: 'space-around',
                                        borderBottom: '2px solid #f1f5f9',
                                        paddingBottom: '15px',
                                        gap: '10px',
                                        flexWrap: 'wrap',
                                        maxWidth: '100%',
                                        boxSizing: 'border-box',
                                    }}
                                >
                                    {currentData.monthlyFlow.map((m, i) => (
                                        <div
                                            key={i}
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                position: 'relative',
                                                minWidth: 90,
                                                boxSizing: 'border-box',
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '15px' }}>
                                                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px' }}>
                                                    <Bar h={(m.actualHires / maxVal) * 200} color="#204385" w="28px" />
                                                    <Bar h={(m.predHires / maxVal) * 200} color="#204385" w="28px" isPred />
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px' }}>
                                                    <Bar h={(m.actualExits / maxVal) * 200} color="#ee645f" w="28px" />
                                                    <Bar h={(m.predExits / maxVal) * 200} color="#ee645f" w="28px" isPred />
                                                </div>
                                            </div>
                                            <span
                                                style={{
                                                    marginTop: '15px',
                                                    fontWeight: 900,
                                                    color: '#1e293b',
                                                    fontSize: '0.9rem',
                                                }}
                                            >
                                                {m.name}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Recommandations Stratégiques */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', maxWidth: '100%' }}>
                                <div
                                    style={{
                                        background: '#204385',
                                        color: '#fff',
                                        padding: '20px',
                                        borderRadius: '15px',
                                        boxShadow: '0 4px 10px rgba(32, 67, 133, 0.2)',
                                        maxWidth: '100%',
                                        boxSizing: 'border-box',
                                    }}
                                >
                                    <h3
                                        style={{
                                            margin: '0 0 10px 0',
                                            fontSize: '0.8rem',
                                            fontWeight: 900,
                                            textTransform: 'uppercase',
                                        }}
                                    >
                                        🎯 Action Recommandée
                                    </h3>
                                    <p style={{ margin: 0, fontSize: '0.9rem', lineHeight: '1.5', fontWeight: 500 }}>
                                        {currentData.recommendedAction}
                                    </p>
                                </div>

                                <div
                                    style={{
                                        background: '#fff',
                                        padding: '20px',
                                        borderRadius: '15px',
                                        border: '1px solid #e2e8f0',
                                        maxWidth: '100%',
                                        boxSizing: 'border-box',
                                    }}
                                >
                                    <h3
                                        style={{
                                            margin: '0 0 12px 0',
                                            fontSize: '0.75rem',
                                            fontWeight: 900,
                                            color: '#64748b',
                                            textTransform: 'uppercase',
                                        }}
                                    >
                                        ⚠️ Alertes de Flux
                                    </h3>
                                    {currentData.monthlyFlow.map((m, i) => (
                                        <div
                                            key={i}
                                            style={{
                                                marginBottom: '10px',
                                                paddingBottom: '10px',
                                                borderBottom: i < 2 ? '1px solid #f1f5f9' : 'none',
                                            }}
                                        >
                                            <div style={{ fontWeight: 800, fontSize: '0.85rem' }}>
                                                {m.name} : {m.alert}
                                            </div>
                                            <div
                                                style={{
                                                    fontSize: '0.75rem',
                                                    color: m.predExits > m.predHires ? '#ef4444' : '#10b981',
                                                }}
                                            >
                                                Ratio P/R : {((m.predHires / m.predExits) || 0).toFixed(2)}x
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

// ============================================
// Subcomponents for FORECAST
// ============================================
const Bar = ({ h, color, w, isPred }) => (
    <div
        style={{
            height: `${h}px`,
            width: w,
            background: isPred ? `${color}10` : color,
            border: isPred ? `3px dashed ${color}` : 'none',
            borderRadius: '6px 6px 0 0',
            transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
            boxSizing: 'border-box',
        }}
    />
);

const KPICard = ({ title, data, unit, invert }) => {
    const isBetter = invert ? data.vN <= data.vN1 : data.vN >= data.vN1;
    const color = isBetter ? '#10b981' : '#ef4444';

    return (
        <div
            style={{
                background: '#fff',
                padding: '18px',
                borderRadius: '15px',
                border: `1px solid ${isBetter ? '#e2e8f0' : '#fecaca'}`,
                borderTop: `4px solid ${color}`,
                boxSizing: 'border-box',
                maxWidth: '100%',
            }}
        >
            <div
                style={{
                    fontSize: '0.65rem',
                    fontWeight: 900,
                    color: '#64748b',
                    textTransform: 'uppercase',
                    marginBottom: '8px',
                }}
            >
                {title}
            </div>
            <div
                style={{
                    fontSize: '1.8rem',
                    fontWeight: 600,
                    color: '#0f172a',
                    marginBottom: '6px',
                    wordBreak: 'break-word',
                }}
            >
                {data.vN}
                {unit}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 900, color }}>
                    {isBetter ? '✓ OPTIMISÉ' : '⚠ ALERTE'}
                </span>
                <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                    vs {data.vN1}
                    {unit} (N-1)
                </span>
            </div>
            <div
                style={{
                    background: '#f8fafc',
                    padding: '10px',
                    borderRadius: '10px',
                    fontSize: '0.7rem',
                    color: '#475569',
                    lineHeight: '1.4',
                    borderLeft: `3px solid ${color}`,
                    boxSizing: 'border-box',
                }}
            >
                <strong>Justification :</strong> {data.just}
            </div>
        </div>
    );
};

const LegendItem = ({ c, l, dotted }) => (
    <div
        style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '0.75rem',
            fontWeight: 800,
            color: '#475569',
            whiteSpace: 'nowrap',
        }}
    >
        <div
            style={{
                width: '14px',
                height: '14px',
                background: dotted ? 'transparent' : c,
                border: dotted ? '2px dashed #64748b' : 'none',
                borderRadius: '3px',
            }}
        />
        {l}
    </div>
);

export default DashboardRecrutementRH;
